<script setup lang="ts">
import TheAdditionalInstructions from '@/components/Menus/Filters/TheAdditionalInstructions.vue';
import TheDietFilters from '@/components/Menus/Filters/TheDietFilters.vue';
import TheMainIngredient from '@/components/Menus/Filters/TheMainIngredient.vue';
import TheMaxPrice from '@/components/Menus/Filters/TheMaxPrice.vue';
import TheMenuDishType from '@/components/Menus/Filters/TheMenuDishType.vue';
import TheMenuHotCold from '@/components/Menus/Filters/TheMenuHotCold.vue';
import TheMenuInspirations from '@/components/Menus/Filters/TheMenuInspirations.vue';
import TheMenuRegionInspiration from '@/components/Menus/Filters/TheMenuRegionInspiration.vue';
import TheMenuSeason from '@/components/Menus/Filters/TheMenuSeason.vue';
import TheMenuSweetSalt from '@/components/Menus/Filters/TheMenuSweetSalt.vue';
import TheMenuTitle from '@/components/Menus/Filters/TheMenuTitle.vue';
import { useAccordionScroll } from '@/composables/useAccordionScroll';
import { useFetch } from '@/composables/useFetch';
import { useOnboarding } from '@/composables/useOnboarding';
import { type MessageSchema } from '@/i18n';
import { getMockDishTitlesByType } from '@/onboarding/mockData';
import { useDishesStore } from '@/stores/dishesStore';
import { usePlanningStore } from '@/stores/planningStore';
import { useUserStore } from '@/stores/useUserStore';
import type { GenerationType } from '@/types/api';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Button from 'primevue/button';
import TabPanel from 'primevue/tabpanel';
import TabView from 'primevue/tabview';
import { useToast } from 'primevue/usetoast';
import { onMounted, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const router = useRouter();
const toast = useToast();
const user = ref(useUserStore().user);
const dishesStore = ref(useDishesStore());
const planningStore = usePlanningStore();

const { isOnboardingActive } = useOnboarding();

const { isFetching } = useFetch();
const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const tabs: Ref<GenerationType[]> = ref(['from_title', 'with_main_product', 'quick']);
const activeTab: Ref<number> = ref(tabs.value.indexOf(dishesStore.value.generationParams.generation_type));

// Accordion scroll-into-view logic
const { activeIndex: filtersActiveIndex, setHeaderRef: setFiltersHeaderRef } = useAccordionScroll<number[]>([]);

watch(activeTab, () => {
  dishesStore.value.generationParams.generation_type = tabs.value[activeTab.value];
});

watch(
  () => dishesStore.value.generationParams.generation_type,
  (newType) => {
    activeTab.value = tabs.value.indexOf(newType);
  },
);

const emit = defineEmits<{
  (e: 'startGeneration'): void;
  (e: 'generationComplete'): void;
}>();

async function getDishes() {
  console.log('getDishes called');
  console.log('isOnboardingActive: ', isOnboardingActive.value);
  if (isOnboardingActive.value) {
    console.log('[Onboarding] Appel IA intercepté (getDishes). Utilisation de données mock.');
    dishesStore.value.isGenerating = true;

    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Récupérer le type de plat sélectionné par l'utilisateur
    const selectedDishType = dishesStore.value.generationParams.dish_type || 'main';
    const dishTitlesForType = getMockDishTitlesByType(selectedDishType as 'main' | 'starter' | 'dessert');

    console.log(`[Onboarding] Utilisation des plats de type: ${selectedDishType}`, dishTitlesForType);
    dishesStore.value.dishTitles = [...dishTitlesForType];
    dishesStore.value.isGenerating = false;

    router.push({ path: '/menu/results' });
    return;
  }
  if (!user.value) return;
  if (user.value.restaurants.length == 0) {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('auth.user.noRestaurantConnected'),
      life: 3000,
    });
    return;
  }

  if (!useUserStore().validateUserCanGenerate()) {
    return null;
  }

  console.log('Generating dishes with params: ', dishesStore.value.generationParams);

  dishesStore.value.cleanGenerationParams();
  console.log('cleaned params: ', dishesStore.value.generationParams);

  if (dishesStore.value.generationParams.generation_type == 'from_title' && dishesStore.value.generationParams.custom_title == '') {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('creationFilters.error.theFieldIsRequired', {
        field: t('creationFilters.menuTitle'),
      }),
      life: 3000,
    });
    return;
  } else if (dishesStore.value.generationParams.generation_type == 'with_main_product') {
    if (dishesStore.value.generationParams.main_product == '' && !dishesStore.value.generationParams.img_product) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('creationFilters.error.theFieldIsRequired', {
          field: t('creationFilters.mainProduct'),
        }),
        life: 3000,
      });
      return;
    }
  } else if (dishesStore.value.generationParams.generation_type == 'quick' && dishesStore.value.generationParams.dish_type == '') {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('creationFilters.error.theFieldIsRequired', {
        field: t('creationFilters.dishType.label'),
      }),
      life: 3000,
    });
    return;
  }
  const img_product = dishesStore.value.generationParams.img_product;
  dishesStore.value.lastGenerationParams = JSON.parse(JSON.stringify(dishesStore.value.generationParams));
  dishesStore.value.lastGenerationParams.img_product = img_product ? img_product : undefined;
  if (planningStore.choosingDishToPlan) {
    emit('startGeneration');
    await dishesStore.value.generateDishes(dishesStore.value.lastGenerationParams);
    emit('generationComplete');
  } else {
    router.push({ path: '/menu/results' });
    dishesStore.value.generateDishes(dishesStore.value.lastGenerationParams);
  }
}

onMounted(() => {
  const queryType = router.currentRoute.value.query.type;

  // Check if queryType is a string and is one of the allowed generation types
  if (typeof queryType === 'string' && tabs.value.includes(queryType as GenerationType)) {
    dishesStore.value.generationParams.generation_type = queryType as GenerationType;
    activeTab.value = tabs.value.indexOf(queryType as GenerationType);
  }
  // console.log('Generation params : ', dishesStore.value.generationParams)
});
</script>

<template>
  <main>
    <div v-show="!dishesStore.isGenerating">
      <TabView v-model:active-index="activeTab" :pt="{ inkbar: { class: 'hidden' } }" :pt-options="{ mergeProps: true }">
        <TabPanel
          :pt="{
            header: { class: '!w-1/2' },
            headerAction: {
              class: '!justify-center',
              id: 'tab-button-enonce',
            },
            content: { class: '!px-0' },
          }"
          :pt-options="{ mergeProps: true }"
          :header="t('creationFilters.tabs.fromTitle')"
        >
          <div>
            <Button :label="t('creationFilters.resetForm')" text class="!w-full" @click="dishesStore.resetGenerationParams()" />
            <TheMenuTitle v-if="dishesStore.generationParams.generation_type === 'from_title'" />
          </div>
        </TabPanel>
        <TabPanel
          :header="t('creationFilters.tabs.mainProduct')"
          :pt="{
            header: { class: '!w-1/2' },
            headerAction: {
              class: '!justify-center',
              id: 'tab-button-produits',
            },
            content: { class: '!px-0' },
          }"
        >
          <div>
            <Button :label="t('creationFilters.resetForm')" text class="!w-full" @click="dishesStore.resetGenerationParams()" />
            <TheMainIngredient />
          </div>
        </TabPanel>
        <TabPanel
          :header="t('creationFilters.tabs.quick')"
          :pt="{
            header: { class: '!w-1/2' },
            headerAction: {
              class: '!justify-center',
              id: 'tab-button-quick',
            },
            content: { class: '!px-0' },
          }"
        >
          <div>
            <Button :label="t('creationFilters.resetForm')" text class="!w-full" @click="dishesStore.resetGenerationParams()" />
            <TheMenuDishType />
          </div>
        </TabPanel>
      </TabView>
      <Accordion v-model:active-index="filtersActiveIndex" multiple class="mb-6">
        <AccordionTab>
          <template #header>
            <div :ref="(el) => setFiltersHeaderRef(el as HTMLElement | null, 0)">
              {{ t('creationFilters.categories.specs') }}
            </div>
          </template>
          <TheMenuHotCold />
          <TheMenuSweetSalt />
          <TheMaxPrice />
          <TheMenuInspirations />
          <TheMenuRegionInspiration />
          <TheMenuSeason />
          <!-- <TheMenuProductPreferences /> -->
          <TheAdditionalInstructions />
        </AccordionTab>
        <AccordionTab>
          <template #header>
            <div :ref="(el) => setFiltersHeaderRef(el as HTMLElement | null, 1)">
              {{ t('creationFilters.categories.diet') }}
            </div>
          </template>
          <TheDietFilters />
        </AccordionTab>
      </Accordion>
      <!-- <TheAITemperature v-if="useEnvStore().environment == 'local' || useEnvStore().environment == 'development'"
        @update-ai-temperature="useDishesStore().generationParams.ai_temperature = $event" /> -->

      <Button id="createButton" :label="t('creationFilters.button')" class="w-full" size="large" :disabled="isFetching" @click="getDishes()" />
    </div>

    <!-- <div v-if="isFetching && isGenerating">
      <LoadingView :message="t('creationFilters.loadingLabel')" :generation="true" />
    </div> -->
  </main>
</template>
